import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { setPasswordErrorMessages } from '@pages/set-password/constants/set-password-errors.constant';
import { InvitationResponse } from '@pages/set-password/models/invitation-response.interface';
import { SetPasswordError } from '@pages/set-password/models/set-password-error.enum';
import { SetPasswordStep } from '@pages/set-password/models/set-password-step.enum';
import { SetPasswordService } from '@pages/set-password/services/set-password.service';
import { SetPassword, SwitchToExpiredLinkStep, ValidateInvitation } from '@pages/set-password/store/actions/set-password.actions';
import { EMPTY, Observable } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';

interface SetPasswordStateModel {
  currentStep?: SetPasswordStep;
  isLoading: boolean;
  minimumPasswordLength?: number;
  username?: string;
  organizationName?: string;
  expiredLinkDescriptionKey?: string;
  errorMessageTranslationKey?: string;
}

@State<SetPasswordStateModel>({
  name: 'setPassword',
  defaults: {
    isLoading: false,
  },
})
@Injectable()
export class SetPasswordState {
  private readonly service = inject(SetPasswordService);

  @Selector()
  static isLoading(state: SetPasswordStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  static currentStep(state: SetPasswordStateModel): SetPasswordStep | undefined {
    return state.currentStep;
  }

  @Selector()
  static minimumPasswordLength(state: SetPasswordStateModel): number | undefined {
    return state.minimumPasswordLength;
  }

  @Selector()
  static username(state: SetPasswordStateModel): string | undefined {
    return state.username;
  }

  @Selector()
  static organizationName(state: SetPasswordStateModel): string | undefined {
    return state.organizationName;
  }

  @Selector()
  static expiredLinkDescriptionKey(state: SetPasswordStateModel): string | undefined {
    return state.expiredLinkDescriptionKey;
  }

  @Selector()
  static errorMessageTranslationKey(state: SetPasswordStateModel): string | undefined {
    return state.errorMessageTranslationKey;
  }

  @Action(ValidateInvitation)
  validateInvitation(
    context: StateContext<SetPasswordStateModel>,
    { invitationToken }: ValidateInvitation
  ): Observable<InvitationResponse> {
    context.patchState({ isLoading: true, errorMessageTranslationKey: undefined });

    return this.service.validateInvitationCode(invitationToken).pipe(
      tap(({ organizationPasswordPolicy, invitationDetails }) => {
        context.patchState({
          minimumPasswordLength: organizationPasswordPolicy.minimumPasswordLength,
          username: invitationDetails.username,
          organizationName: invitationDetails.organizationName,
          currentStep: SetPasswordStep.Set,
        });
      }),
      catchError((errorResponse: HttpErrorResponse) => {
        if (errorResponse?.status !== HttpStatusCode.BadRequest) {
          context.patchState({ currentStep: SetPasswordStep.ConnectionError });
          return EMPTY;
        }

        if (!this.isSetPasswordError(errorResponse.error)) {
          this.handleUnexpectedError(context);
          return EMPTY;
        }

        const expiredLinkDescriptionKey = this.getExpiredLinkDescriptionKey(errorResponse.error);
        context.patchState({ currentStep: SetPasswordStep.Expired, expiredLinkDescriptionKey });

        return EMPTY;
      }),
      finalize(() => context.patchState({ isLoading: false }))
    );
  }

  @Action(SetPassword)
  setPassword(context: StateContext<SetPasswordStateModel>, { request }: SetPassword): Observable<void> {
    context.patchState({ isLoading: true, errorMessageTranslationKey: undefined });

    return this.service.setPassword(request).pipe(
      tap(() => {
        context.patchState({ currentStep: SetPasswordStep.Success });
      }),
      catchError((errorResponse: HttpErrorResponse) => {
        if (errorResponse?.status !== HttpStatusCode.BadRequest) {
          context.patchState({ currentStep: SetPasswordStep.ConnectionError });
          return EMPTY;
        }

        const errorCode = errorResponse.error;

        if (!this.isSetPasswordError(errorCode)) {
          this.handleUnexpectedError(context);
          return EMPTY;
        }

        if (errorCode === SetPasswordError.InvalidUserInvitationToken) {
          context.patchState({
            currentStep: SetPasswordStep.Expired,
            expiredLinkDescriptionKey: 'expired-link.description.default',
          });
        }

        const errorMessageTranslationKey = setPasswordErrorMessages[errorCode];
        if (errorMessageTranslationKey) {
          context.patchState({ errorMessageTranslationKey });
        }

        return EMPTY;
      }),
      finalize(() => context.patchState({ isLoading: false }))
    );
  }

  @Action(SwitchToExpiredLinkStep)
  switchToExpiredLinkStep(context: StateContext<SetPasswordStateModel>): void {
    this.handleUnexpectedError(context);
  }

  private handleUnexpectedError(context: StateContext<SetPasswordStateModel>): void {
    context.patchState({
      currentStep: SetPasswordStep.Expired,
      expiredLinkDescriptionKey: 'expired-link.description.default',
    });
  }

  private getExpiredLinkDescriptionKey(error: unknown): string {
    if (this.isSetPasswordError(error) && error === SetPasswordError.InvalidInvitationToken) {
      return 'expired-link.description.default';
    }
    return 'expired-link.description.invitation';
  }

  private isSetPasswordError(error: unknown): error is SetPasswordError {
    return Object.values(SetPasswordError).includes(error as SetPasswordError);
  }
}
